import type { JSONContent } from "@tiptap/core";
import { parseNotesDocument } from "@/lib/notes/tiptap-document";

export type MarkdownBlock =
  | { kind: "heading"; level: 1 | 2 | 3 | 4; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "blockquote"; text: string }
  | { kind: "code"; text: string; language: string | null }
  | { kind: "rule" }
  | {
      kind: "list";
      items: Array<{
        text: string;
        ordered: boolean;
        checked: boolean | null;
        indent: number;
        /** Numbering offset of the containing ordered list; absent = 1. */
        start?: number;
      }>;
    };

/**
 * Convert a Tiptap notes document into the flat block sequence consumed by
 * the DOCX and PDF renderers. Walks the JSON tree directly so the renderers
 * stay aligned with whatever `parseNotesDocument` already validated.
 */
export function notesDocumentToMarkdownBlocks(doc: unknown): MarkdownBlock[] {
  const parsed = parseNotesDocument(doc);
  if (!parsed.success) return [];
  return (parsed.doc.content ?? []).flatMap(topLevelNodeToBlocks);
}

function topLevelNodeToBlocks(node: JSONContent): MarkdownBlock[] {
  switch (node.type) {
    case "paragraph": {
      const text = inlineText(node);
      if (!text) return [];
      return [{ kind: "paragraph", text }];
    }
    case "heading": {
      // Schema guarantees attrs.level ∈ [1, 4] when set; default to 1 when
      // missing (Tiptap may omit attrs for the default heading level).
      const rawLevel = node.attrs?.level;
      const level = (typeof rawLevel === "number" ? rawLevel : 1) as 1 | 2 | 3 | 4;
      return [{ kind: "heading", level, text: inlineText(node) }];
    }
    case "blockquote":
      return [{ kind: "blockquote", text: blockquoteText(node) }];
    case "codeBlock":
      return [
        {
          kind: "code",
          language:
            typeof node.attrs?.language === "string" && node.attrs.language.length > 0
              ? node.attrs.language
              : null,
          text: codeBlockText(node),
        },
      ];
    case "horizontalRule":
      return [{ kind: "rule" }];
    case "bulletList":
    case "orderedList":
    case "taskList": {
      const items = flattenListItems(node, 0);
      if (items.length === 0) return [];
      return [{ kind: "list", items }];
    }
    default:
      return [];
  }
}

function inlineText(node: JSONContent): string {
  return collectInline(node.content);
}

// Delimiters for the style marks the notes schema allows. Iteration order is
// the canonical nesting order (outermost first). Code is handled separately:
// its fence width depends on the content.
const STYLE_DELIMITERS = new Map<string, string>([
  ["bold", "**"],
  ["italic", "*"],
  ["strike", "~~"],
]);

const LINK_ENTRY_PREFIX = "link:";
const CODE_ENTRY_PREFIX = "code";

/**
 * Canonical open-mark signature for a text node: link outermost (the href
 * rides in the entry, so different destinations never merge), styles in
 * fixed order, code innermost (its content is verbatim — nothing may nest
 * inside). TipTap stores marks in insertion order, so they must be
 * normalized before comparing adjacent nodes.
 */
function markSignature(child: JSONContent): string[] {
  const marks = child.marks;
  if (!marks?.length) return [];
  const signature: string[] = [];
  const href = marks.find((mark) => mark.type === "link")?.attrs?.href;
  if (typeof href === "string" && href.length > 0) {
    signature.push(`${LINK_ENTRY_PREFIX}${href}`);
  }
  for (const kind of STYLE_DELIMITERS.keys()) {
    if (marks.some((mark) => mark.type === kind)) signature.push(kind);
  }
  if (marks.some((mark) => mark.type === "code")) {
    // Fence one backtick longer than the longest run inside the content, so
    // a literal backtick in code can't close the span early.
    const runs = (child.text ?? "").match(/`+/g);
    const fence = runs ? Math.max(...runs.map((run) => run.length)) + 1 : 1;
    signature.push(fence > 1 ? `${CODE_ENTRY_PREFIX}:${fence}` : CODE_ENTRY_PREFIX);
  }
  return signature;
}

function codeFence(entry: string): string {
  return "`".repeat(
    entry === CODE_ENTRY_PREFIX ? 1 : Number(entry.slice(CODE_ENTRY_PREFIX.length + 1)),
  );
}

// A plain markdown link destination ends at the first ")" or whitespace;
// percent-encode those so any allowed href survives the round-trip. Parens
// are mapped by hand — encodeURIComponent leaves them alone — while
// whitespace goes through encodeURIComponent for correct UTF-8 bytes
// (U+00A0, U+3000, … are multi-byte).
function encodeMarkdownHref(href: string): string {
  return href
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/\s/g, (char) => encodeURIComponent(char));
}

function markOpener(entry: string): string {
  if (entry.startsWith(LINK_ENTRY_PREFIX)) return "[";
  if (entry.startsWith(CODE_ENTRY_PREFIX)) return codeFence(entry);
  return STYLE_DELIMITERS.get(entry) ?? "";
}

function markCloser(entry: string): string {
  if (entry.startsWith(LINK_ENTRY_PREFIX)) {
    return `](${encodeMarkdownHref(entry.slice(LINK_ENTRY_PREFIX.length))})`;
  }
  if (entry.startsWith(CODE_ENTRY_PREFIX)) return codeFence(entry);
  return STYLE_DELIMITERS.get(entry) ?? "";
}

// Backslash-escape our own delimiters in regular text so literal characters
// survive both markdown rendering and the DOCX re-scan.
function escapeInlineText(text: string): string {
  return text.replace(/[\\`*~[\]]/g, "\\$&");
}

// Code spans are verbatim — escapes don't work inside them. CommonMark
// strips one space pair, and a backtick at either end would fuse with the
// fence, so those ends get padded instead. All-whitespace content is exempt:
// the strip rule never fires on it, so padding would accrete spaces.
function padCodeText(text: string): string {
  return /^[` ]|[` ]$/.test(text) && text.trim() ? ` ${text} ` : text;
}

/**
 * Flatten inline children to markdown-formatted text: style marks come back
 * as markdown delimiters and links as `[label](href)` so exports never
 * silently drop formatting — the href especially is unrecoverable once the
 * tab closes.
 */
function collectInline(children: JSONContent[] | undefined): string {
  if (!children?.length) return "";
  // Empty text nodes render nothing but would poison the lookahead below.
  const nodes = children.filter(
    (child) => child.type !== "text" || (child.text ?? "").length > 0,
  );
  const signatures = nodes.map((child) =>
    child.type === "text" ? markSignature(child) : null,
  );
  const parts: string[] = [];
  const open: string[] = [];

  const closeDownTo = (depth: number) => {
    while (open.length > depth) {
      const entry = open.pop();
      if (entry) parts.push(markCloser(entry));
    }
  };

  for (let index = 0; index < nodes.length; index += 1) {
    const child = nodes[index];
    if (child.type === "hardBreak") {
      // Close spans at the break: markdownBlocksToString injects "> " and
      // list prefixes at "\n", which would otherwise land inside the span.
      closeDownTo(0);
      parts.push("\n");
      continue;
    }
    if (child.type !== "text") {
      closeDownTo(0);
      parts.push(collectInline(child.content));
      continue;
    }
    const text = child.text ?? "";
    const signature = signatures[index] ?? [];
    const desired = new Set(signature);
    let keep = 0;
    while (keep < open.length && desired.has(open[keep])) keep += 1;
    closeDownTo(keep);
    const toOpen = signature.filter((entry) => !open.includes(entry));
    // One-node lookahead: marks that continue into the next text node open
    // outermost, so closing this node's extras never splits them — an italic
    // phrase with a bold word emits `*a**b***`, not the fused `*a****b***`.
    const nextSignature = signatures[index + 1];
    if (nextSignature?.length) {
      const persisting = new Set(nextSignature);
      toOpen.sort((a, b) => Number(persisting.has(b)) - Number(persisting.has(a)));
    }
    for (const entry of toOpen) {
      open.push(entry);
      parts.push(markOpener(entry));
    }
    parts.push(
      signature.some((entry) => entry.startsWith(CODE_ENTRY_PREFIX))
        ? padCodeText(text)
        : escapeInlineText(text),
    );
  }

  closeDownTo(0);
  return parts.join("");
}

function blockquoteText(node: JSONContent): string {
  return (node.content ?? [])
    .map(blockquoteChildText)
    .filter((line) => line.length > 0)
    .join("\n");
}

function blockquoteChildText(child: JSONContent): string {
  if (child.type === "paragraph" || child.type === "heading") return inlineText(child);
  if (child.type === "blockquote") return blockquoteText(child);
  if (
    child.type === "bulletList" ||
    child.type === "orderedList" ||
    child.type === "taskList"
  ) {
    // One line per list item — `collectInline` joins siblings with "" and
    // would mash the items into a single unseparated run.
    return (child.content ?? [])
      .map((item) =>
        (item.content ?? [])
          .map(blockquoteChildText)
          .filter((line) => line.length > 0)
          .join("\n"),
      )
      .filter((line) => line.length > 0)
      .join("\n");
  }
  return collectInline(child.content);
}

function codeBlockText(node: JSONContent): string {
  return (node.content ?? [])
    .map((child) => (child.type === "text" ? child.text ?? "" : ""))
    .join("");
}

type FlatListItem = Extract<MarkdownBlock, { kind: "list" }>["items"][number];

function flattenListItems(listNode: JSONContent, indent: number): FlatListItem[] {
  const ordered = listNode.type === "orderedList";
  const isTaskList = listNode.type === "taskList";
  // Carried on every item of this list so the renderers' per-level counters
  // can seed from it whenever the level is (re-)entered.
  const start =
    ordered && typeof listNode.attrs?.start === "number" ? listNode.attrs.start : 1;
  const items: FlatListItem[] = [];

  for (const item of listNode.content ?? []) {
    if (item.type !== "listItem" && item.type !== "taskItem") continue;

    const children = item.content ?? [];
    const labelChild = children.find(
      (child) => child.type === "paragraph" || child.type === "heading"
    );
    const text = labelChild ? inlineText(labelChild) : "";
    const checked = isTaskList ? Boolean(item.attrs?.checked) : null;

    items.push({ text, ordered, checked, indent, ...(start !== 1 ? { start } : {}) });

    // Nested lists inside this item are flattened with one more indent level.
    for (const child of children) {
      if (
        child.type === "bulletList" ||
        child.type === "orderedList" ||
        child.type === "taskList"
      ) {
        items.push(...flattenListItems(child, indent + 1));
      }
    }
  }

  return items;
}

